import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { industryWorks, IndustryWork as IndustryWorkType } from '../data/IndustryWorkData';
import Button from '../components/Button';
const IndustryWork = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const work = industryWorks.find(w => w.id === parseInt(id || '1'));
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);
  if (!work) {
    return <div className="bg-gray-900 text-white min-h-screen flex items-center justify-center" data-id="element-470">
        <div className="text-center" data-id="element-471">
          <h1 className="text-2xl font-bold mb-4" data-id="element-472">Industry Work Not Found</h1>
          <Link to="/product-deep-dives" className="text-indigo-400 hover:text-indigo-300" data-id="element-473">
            Back to Product Deep Dives
          </Link>
        </div>
      </div>;
  }
  return <div className="bg-gray-900 text-white" data-id="element-474">
      {/* Hero section */}
      <div className="relative border-b border-gray-800" data-id="element-475">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-id="element-476">
          <Link to="/product-deep-dives" className="inline-flex items-center text-indigo-400 hover:text-indigo-300 mb-6 group" data-id="element-477">
            <ArrowLeftIcon className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-478" />
            Back to Product Deep Dives
          </Link>
          <div className="text-center md:text-left text-white" data-id="element-479">
            <p className="text-indigo-400 text-sm font-semibold tracking-wide uppercase" data-id="element-480">
              Industry Work • {work.company}
            </p>
            <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl" data-id="element-481">
              {work.title}
            </h1>
            {work.subtitle && <p className="mt-3 text-xl text-gray-300" data-id="element-482">{work.subtitle}</p>}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-483">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-484">
          {/* Sidebar */}
          <div className="lg:col-span-1" data-id="element-485">
            <div className="sticky top-24" data-id="element-486">
              <div className="flex items-start mb-6" data-id="element-487">
                <div className="w-16 h-16 rounded-md overflow-hidden bg-white p-2 flex-shrink-0" data-id="element-488">
                  <img src={work.imageUrl} alt={work.company} className="w-full h-full object-contain" data-id="element-489" />
                </div>
              </div>
              <h2 className="text-lg font-medium text-white mb-4" data-id="element-490">
                Quick Navigation
              </h2>
              <nav className="space-y-1" aria-label="Sidebar" data-id="element-491">
                <a href="#problem-context" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-492">
                  Problem Context
                </a>
                <a href="#research-insights" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-493">
                  Research & Insights
                </a>
                <a href="#product-goal" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-494">
                  Product Goal
                </a>
                <a href="#solution-approach" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-495">
                  Solution Approach
                </a>
                <a href="#impact" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-496">
                  Impact
                </a>
                <a href="#product-thinking" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-497">
                  Product Thinking Snapshot
                </a>
                <a href="#tools" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-498">
                  Tools Used
                </a>
                <a href="#reflection" className="text-gray-300 hover:text-indigo-400 hover:bg-gray-800 flex items-center px-3 py-2 text-sm font-medium rounded-md" data-id="element-499">
                  Reflection
                </a>
              </nav>
              <div className="mt-8 flex flex-wrap gap-2" data-id="element-500">
                {work.tags.map((tag, index) => <span key={index} className="bg-gray-800 text-indigo-300 text-xs px-2 py-1 rounded-full border border-gray-700" data-id="element-501">
                    {tag}
                  </span>)}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-502">
            {/* Problem Context */}
            <section id="problem-context" data-id="element-503">
              <h2 className="text-2xl font-bold text-white mb-4" data-id="element-504">
                Problem Context
              </h2>
              <p className="text-gray-300 leading-relaxed whitespace-pre-line" data-id="element-505">
                {work.problemContext}
              </p>
            </section>

            {/* Research & Insights */}
            <section id="research-insights" data-id="element-506">
              <h2 className="text-2xl font-bold text-white mb-4" data-id="element-507">
                Research & Insights
              </h2>
              <p className="text-gray-300 mb-3" data-id="element-508">
                {work.researchInsights.description}
              </p>
              {work.researchInsights.sources && <ul className="list-decimal pl-5 space-y-1 text-gray-300 mb-6" data-id="element-509">
                  {work.researchInsights.sources.map((source, i) => <li key={i} data-id="element-510">{source}</li>)}
                </ul>}
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700" data-id="element-511">
                {work.researchInsights.sources && <p className="text-gray-300 font-medium mb-3" data-id="element-512">
                    Key insights:
                  </p>}
                <ul className="list-disc pl-5 space-y-2 text-gray-300" data-id="element-513">
                  {work.researchInsights.keyInsights.map((insight, i) => <li key={i} data-id="element-514">{insight}</li>)}
                </ul>
              </div>
            </section>

            {/* Product Goal */}
            <section id="product-goal" data-id="element-515">
              <h2 className="text-2xl font-bold text-white mb-4" data-id="element-516">
                Product Goal
              </h2>
              <p className="text-gray-300 mb-4" data-id="element-517">
                {work.productGoal.description}
              </p>
              <ul className="list-disc pl-5 space-y-2 text-gray-300" data-id="element-518">
                {work.productGoal.objectives.map((objective, i) => <li key={i} data-id="element-519">{objective}</li>)}
              </ul>
            </section>

            {/* Solution Approach */}
            <section id="solution-approach" data-id="element-520">
              <h2 className="text-2xl font-bold text-white mb-4" data-id="element-521">
                Solution Approach
              </h2>
              <p className="text-gray-300 mb-6" data-id="element-522">
                {work.solutionApproach.description}
              </p>

              <div className="space-y-4" data-id="element-523">
                <h3 className="text-xl font-semibold text-white" data-id="element-524">
                  Key Contributions
                </h3>
                {work.keyContributions.map((contribution, i) => <div key={i} className="bg-gray-800 rounded-lg p-6 border border-gray-700" data-id="element-525">
                    <h4 className="text-lg font-semibold text-indigo-400 mb-3" data-id="element-526">
                      {contribution.title}
                    </h4>
                    <ul className="list-disc pl-5 space-y-2 text-gray-300" data-id="element-527">
                      {contribution.points.map((point, j) => <li key={j} data-id="element-528">{point}</li>)}
                    </ul>
                  </div>)}
              </div>
            </section>

            {/* Impact */}
            <section id="impact" data-id="element-529">
              <h2 className="text-2xl font-bold text-white mb-4" data-id="element-530">Impact</h2>
              <p className="text-gray-300 mb-4" data-id="element-531">
                The new{' '}
                {work.id === 1 ? 'subscription experience' : 'reporting platform'}{' '}
                enabled:
              </p>
              <ul className="list-disc pl-5 space-y-2 text-gray-300" data-id="element-532">
                {work.impact.map((item, i) => <li key={i} data-id="element-533">{item}</li>)}
              </ul>
              {work.id === 2 && <p className="text-gray-400 text-sm mt-4 italic" data-id="element-534">
                  (Impact mentioned at a high level to maintain
                  confidentiality.)
                </p>}
            </section>

            {/* Product Thinking Snapshot */}
            <section id="product-thinking" data-id="element-535">
              <h2 className="text-2xl font-bold text-white mb-4" data-id="element-536">
                Product Thinking Snapshot
              </h2>
              <div className="space-y-4" data-id="element-537">
                {work.productThinkingSnapshot.map((item, i) => <div key={i} className="bg-gray-800 rounded-lg p-4 border border-gray-700" data-id="element-538">
                    <h3 className="text-lg font-semibold text-indigo-400 mb-2" data-id="element-539">
                      {item.title}
                    </h3>
                    <p className="text-gray-300" data-id="element-540">{item.description}</p>
                  </div>)}
              </div>
            </section>

            {/* Tools Used */}
            <section id="tools" data-id="element-541">
              <h2 className="text-2xl font-bold text-white mb-4" data-id="element-542">Tools Used</h2>
              <div className="flex flex-wrap items-center gap-4" data-id="element-543">
                {work.tools.map((tool, i) => <div key={i} className="flex items-center gap-3 bg-gray-800 rounded-lg px-5 py-4 border border-gray-700" data-id="element-544">
                    <img src={tool.icon} alt={tool.name} className="w-8 h-8" data-id="element-545" />
                    <span className="text-gray-300 font-medium" data-id="element-546">
                      {tool.name}
                    </span>
                  </div>)}
              </div>
            </section>

            {/* Reflection */}
            <section id="reflection" data-id="element-547">
              <h2 className="text-2xl font-bold text-white mb-4" data-id="element-548">Reflection</h2>
              <div className="bg-gray-800 rounded-lg p-6 border border-gray-700" data-id="element-549">
                <p className="text-indigo-300 font-medium leading-relaxed" data-id="element-550">
                  {work.reflection}
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* CTA section */}
      <div className="bg-gray-800 py-12" data-id="element-551">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-552">
          <div className="text-center" data-id="element-553">
            <h2 className="text-3xl font-bold text-white" data-id="element-554">
              Interested in working together?
            </h2>
            <p className="mt-4 text-lg text-gray-300" data-id="element-555">
              Let's discuss how I can bring my product expertise to your team.
            </p>
            <div className="mt-8" data-id="element-556">
              <Button to="/contact" variant="primary" size="lg" data-id="element-557">
                Get in Touch
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default IndustryWork;